import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Loader2,
  Minus,
  Phone,
  Plus,
  ReceiptText,
  Save,
  ShoppingBag,
  Trash2,
  Truck,
  UserRound,
  UtensilsCrossed,
} from "lucide-react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { publicMenuApi } from "../../api";
import { useAlerts } from "../../components/feedback/AlertProvider";
import { useLanguage } from "../../contexts/LanguageContext";
import translateApiError from "../../utils/translateApiError";
import "../../styles/PublicMenu/public-order-checkout.css";
const PUBLIC_MENU_CART_PREFIX = "public_menu_cart_";
const PUBLIC_MENU_ORDERS_PREFIX = "public_menu_orders_";
const DEFAULT_PUBLIC_MENU_THEME = {
  theme: "light",
  primary_color: "#51766E",
  background_color: "#F8FAF9",
  text_color: "#333333",
  surface_color: "#FFFFFF",
  accent_color: "#D7E3E0",
  border_radius: "large",
  card_style: "elevated",
  card_shadow: "soft",
  font_family: "system",
};
const hexToRgb = (color, fallback) => {
  if (!color) return fallback;
  const trimmed = String(color).trim();
  if (trimmed.startsWith("rgb")) {
    const matches = trimmed.match(/\d+(?:\.\d+)?/g);
    if (matches?.length >= 3) return matches.slice(0, 3).join(", ");
  }
  const normalized = trimmed.replace("#", "");
  const fullHex =
    normalized.length === 3
      ? normalized.split("").map((char) => `${char}${char}`).join("")
      : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) return fallback;
  return `${parseInt(fullHex.slice(0, 2), 16)}, ${parseInt(fullHex.slice(2, 4), 16)}, ${parseInt(fullHex.slice(4, 6), 16)}`;
};
const buildCheckoutTheme = (menuData) => {
  const menu = menuData?.menu || {};
  const establishment = menuData?.establishment || {};
  const template =
    menuData?.template ||
    menu?.template ||
    menu?.menu_template ||
    {};
  return {
    ...DEFAULT_PUBLIC_MENU_THEME,
    ...(template?.config || {}),
    ...(establishment?.menu_design_settings || {}),
    ...(menuData?.design_settings || {}),
    ...(menu?.design_settings || {}),
    ...(menu?.menu_design_settings || {}),
  };
};
const isEnabledFlag = (value, defaultValue = false) => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  return ["1", "true", "yes", "on"].includes(String(value).toLowerCase());
};
const translateFallback = (t, key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};
const formatPrice = (value, currency = "MAD") => {
  return `${Number(value || 0).toFixed(2)} ${currency}`;
};
const normalizeBackendFieldName = (fieldName) => {
  if (!fieldName) return "";
  if (fieldName.includes("customer_name")) return "customer_name";
  if (fieldName.includes("customer_phone")) return "customer_phone";
  if (fieldName.includes("table_number")) return "table_number";
  if (fieldName === "items" || fieldName.startsWith("items.")) return "cart";
  return fieldName;
};
const getBackendFieldErrors = (requestError) => {
  const responseErrors = requestError?.response?.data?.errors;
  if (!responseErrors || typeof responseErrors !== "object") {
    return {};
  }
  return Object.entries(responseErrors).reduce((errors, [fieldName, messages]) => {
    const normalizedField = normalizeBackendFieldName(fieldName);
    const message = Array.isArray(messages) ? messages[0] : messages;
    if (normalizedField && message) {
      errors[normalizedField] = String(message);
    }
    return errors;
  }, {});
};
function PublicOrderCheckout() {
  const { establishmentSlug, menuSlug } = useParams();
  const menuStorageId = `${establishmentSlug || ""}__${menuSlug || ""}`;
  const publicMenuPath = `/${encodeURIComponent(establishmentSlug || "")}/${encodeURIComponent(menuSlug || "")}`;
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { showAlert } = useAlerts();
  const outletContext = useOutletContext() || {};
  const cartStorageKey = `${PUBLIC_MENU_CART_PREFIX}${menuStorageId}`;
  const ordersStorageKey = `${PUBLIC_MENU_ORDERS_PREFIX}${menuStorageId}`;
  const [cartItems, setCartItems] = useState([]);
  const [cartLoaded, setCartLoaded] = useState(false);
  const [establishment, setEstablishment] = useState(null);
  const [checkoutTheme, setCheckoutTheme] = useState(DEFAULT_PUBLIC_MENU_THEME);
  const [orderingEnabled, setOrderingEnabled] = useState(false);
  const [loadingContext, setLoadingContext] = useState(true);
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_note: "",
    order_type: "takeaway",
    table_number: "",
  });
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const currency = establishment?.currency || "MAD";
  const checkoutThemeStyle = useMemo(
    () => ({
      "--public-menu-primary": checkoutTheme.primary_color || "#51766E",
      "--public-menu-bg": checkoutTheme.background_color || "#F8FAF9",
      "--public-menu-text": checkoutTheme.text_color || "#333333",
      "--public-menu-surface": checkoutTheme.surface_color || "#FFFFFF",
      "--public-menu-accent": checkoutTheme.accent_color || "#D7E3E0",
      "--public-menu-primary-rgb": hexToRgb(
        checkoutTheme.primary_color,
        "81, 118, 110",
      ),
      "--public-menu-bg-rgb": hexToRgb(
        checkoutTheme.background_color,
        "248, 250, 249",
      ),
      "--public-menu-text-rgb": hexToRgb(
        checkoutTheme.text_color,
        "51, 51, 51",
      ),
      "--public-menu-surface-rgb": hexToRgb(
        checkoutTheme.surface_color,
        "255, 255, 255",
      ),
      "--public-menu-accent-rgb": hexToRgb(
        checkoutTheme.accent_color,
        "215, 227, 224",
      ),
    }),
    [checkoutTheme],
  );
  const checkoutThemeClassName = [
    "checkout-page",
    `checkout-page--theme-${checkoutTheme.theme || "light"}`,
    `checkout-page--radius-${checkoutTheme.border_radius || "large"}`,
    `checkout-page--card-${checkoutTheme.card_style || "elevated"}`,
    `checkout-page--shadow-${checkoutTheme.card_shadow || "soft"}`,
    `checkout-page--font-${checkoutTheme.font_family || "system"}`,
  ].join(" ");
  const totalAmount = useMemo(() => {
    return cartItems.reduce(
      (total, item) => total + Number(item.total_price || 0),
      0,
    );
  }, [cartItems]);
  const cartCount = useMemo(() => {
    return cartItems.reduce(
      (total, item) => total + Number(item.quantity || 0),
      0,
    );
  }, [cartItems]);
  const canSubmitOrder = !saving;
  const notify = (type, message) => {
    showAlert({
      type,
      title: type === "success" ? t("alerts.success") : t("alerts.error"),
      message,
    });
  };
  const getLocalCartItems = () => {
    try {
      return JSON.parse(localStorage.getItem(cartStorageKey) || "[]");
    } catch {
      return [];
    }
  };
  const triggerErrorShake = () => {
    document.body.classList.remove("checkout-error-shake");
    window.requestAnimationFrame(() => {
      document.body.classList.add("checkout-error-shake");
      window.setTimeout(() => {
        document.body.classList.remove("checkout-error-shake");
      }, 480);
    });
  };
  const showErrorResult = (text, nextFieldErrors = {}) => {
    setFieldErrors(nextFieldErrors);
    notify("error", text);
    triggerErrorShake();
  };
  const fetchCheckoutContext = async () => {
    try {
      setLoadingContext(true);
      setCartLoaded(false);
      const localCart = getLocalCartItems();
      setCartItems(localCart);
      setCartLoaded(true);
      const response = await publicMenuApi.getMenu(establishmentSlug, menuSlug);
      const menuData = response.data.data || null;
      const currentMenu = menuData?.menu || null;
      const currentEstablishment = menuData?.establishment || null;
      setCheckoutTheme(buildCheckoutTheme(menuData));
      setEstablishment(currentEstablishment);
      setOrderingEnabled(
        isEnabledFlag(
          currentMenu?.ordering_enabled ??
            currentMenu?.settings?.ordering_enabled ??
            menuData?.ordering_enabled ??
            outletContext?.orderingEnabled ??
            currentEstablishment?.ordering_enabled,
          false,
        ),
      );
    } catch {
      setOrderingEnabled(false);
    } finally {
      setLoadingContext(false);
    }
  };
  useEffect(() => {
    fetchCheckoutContext();
  }, [establishmentSlug, menuSlug, cartStorageKey]);
  useEffect(() => {
    if (!cartLoaded) return;
    localStorage.setItem(cartStorageKey, JSON.stringify(cartItems));
  }, [cartItems, cartStorageKey, cartLoaded]);
  const updateForm = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
    setFieldErrors((current) => {
      if (!current[key]) return current;
      const nextErrors = { ...current };
      delete nextErrors[key];
      return nextErrors;
    });
  };
  const updateOrderType = (type) => {
    updateForm("order_type", type);
    setFieldErrors((current) => {
      const nextErrors = { ...current };
      if (type === "dine_in") {
        delete nextErrors.customer_phone;
      } else {
        delete nextErrors.table_number;
      }
      return nextErrors;
    });
  };
  const updateQuantity = (rowId, quantity) => {
    setCartItems((items) =>
      items.map((item) => {
        if (item.row_id !== rowId) return item;
        const nextQuantity = Math.max(1, Number(quantity || 1));
        return {
          ...item,
          quantity: nextQuantity,
          total_price: Number(item.unit_price || 0) * nextQuantity,
        };
      }),
    );
  };
  const removeItem = (rowId) => {
    setCartItems((items) => items.filter((item) => item.row_id !== rowId));
    setFieldErrors((current) => {
      if (!current.cart || cartItems.length > 1) return current;
      const nextErrors = { ...current };
      delete nextErrors.cart;
      return nextErrors;
    });
  };
  const saveCreatedOrder = (order) => {
    try {
      const existingOrders = JSON.parse(
        localStorage.getItem(ordersStorageKey) || "[]",
      );
      localStorage.setItem(
        ordersStorageKey,
        JSON.stringify([order, ...existingOrders]),
      );
    } catch {
      localStorage.setItem(ordersStorageKey, JSON.stringify([order]));
    }
  };
  const submitOrder = async (event) => {
    event.preventDefault();
    setFieldErrors({});
    if (!orderingEnabled) {
      showErrorResult(
        translateFallback(
          t,
          "publicMenu.checkout.orderingDisabled",
          "Online ordering is disabled for this restaurant.",
        ),
      );
      return;
    }
    const nextFieldErrors = {};
    if (!form.customer_name.trim()) {
      nextFieldErrors.customer_name = t("publicMenu.checkout.validation.name");
    }
    if (form.order_type !== "dine_in" && !form.customer_phone.trim()) {
      nextFieldErrors.customer_phone = t("publicMenu.checkout.validation.phone");
    }
    if (form.order_type === "dine_in" && !form.table_number.trim()) {
      nextFieldErrors.table_number = t("publicMenu.checkout.validation.table");
    }
    if (!cartItems.length) {
      nextFieldErrors.cart = t("publicMenu.checkout.validation.cart");
    }
    if (Object.keys(nextFieldErrors).length > 0) {
      showErrorResult(
        translateFallback(
          t,
          "publicMenu.checkout.validation.form",
          "Please complete the required fields.",
        ),
        nextFieldErrors,
      );
      return;
    }
    try {
      setSaving(true);
      const response = await publicMenuApi.createOrder(establishmentSlug, menuSlug, {
        customer_name: form.customer_name.trim(),
        customer_phone:
        form.order_type === "dine_in" ? null : form.customer_phone.trim(),
        customer_note: form.customer_note.trim() || null,
        order_type: form.order_type,
        table_number:
          form.order_type === "dine_in" ? form.table_number.trim() : null,
        items: cartItems.map((item) => ({
          product_id: item.product_id,
          product_size_id: item.product_size_id || null,
          supplement_ids: item.supplement_ids || [],
          quantity: item.quantity,
          note: item.note || null,
        })),
      });
      const responseData = response.data || {};
      const createdOrder =
        responseData?.data?.order ||
        responseData?.order ||
        responseData?.data ||
        responseData;
      const createdOrderId = createdOrder?.id;
      if (!createdOrderId) {
        console.error("Create order response without usable id:", responseData);
        showErrorResult(
          translateFallback(
            t,
            "orders.messages.unableToCreate",
            "Unable to create the order.",
          ),
        );
        return;
      }
      localStorage.removeItem(cartStorageKey);
      setCartItems([]);
      saveCreatedOrder(createdOrder);
      notify(
        "success",
        translateFallback(
          t,
          "orders.messages.created",
          "Order created successfully.",
        ),
      );
      window.setTimeout(() => {
        navigate(`${publicMenuPath}/orders/${createdOrderId}`);
      }, 650);
    } catch (requestError) {
      const backendFieldErrors = getBackendFieldErrors(requestError);
      const status = requestError?.response?.status;
      const message =
        status === 404
          ? t("publicMenu.checkout.restaurantUnavailable")
          : translateApiError(requestError, t);
      showErrorResult(message, backendFieldErrors);
    } finally {
      setSaving(false);
    }
  };
  if (loadingContext) {
    return (
      <main className={`${checkoutThemeClassName} checkout-page--loading`} style={checkoutThemeStyle}>
        <section className="checkout-loading-card">
          <Loader2 size={34} />
          <h1>{t("publicMenu.checkout.title")}</h1>
          <p>
            {translateFallback(
              t,
              "publicMenu.checkout.loading",
              "Preparing your checkout...",
            )}
          </p>
        </section>
      </main>
    );
  }
  return (
    <main className={checkoutThemeClassName} style={checkoutThemeStyle}>
      <section className="checkout-shell">
        <header className="checkout-hero">
          <div>
            <p>{t("publicMenu.checkout.eyebrow")}</p>
            <h1>{t("publicMenu.checkout.title")}</h1>
            <span>{t("publicMenu.checkout.description")}</span>
          </div>
          <div className="checkout-hero__card">
            <ShoppingBag size={26} />
            <strong>{cartCount}</strong>
            <small>
              {translateFallback(t, "publicMenu.checkout.items", "Items")}
            </small>
          </div>
        </header>
        {!orderingEnabled && (
          <div className="checkout-warning">
            <AlertTriangle size={20} />
            <div>
              <strong>
                {translateFallback(
                  t,
                  "publicMenu.checkout.orderingDisabledTitle",
                  "Online ordering is disabled",
                )}
              </strong>
              <span>
                {translateFallback(
                  t,
                  "publicMenu.checkout.orderingDisabledDescription",
                  "You can review your cart, but this restaurant is not accepting online orders right now.",
                )}
              </span>
            </div>
          </div>
        )}
        <form className="checkout-layout" onSubmit={submitOrder} noValidate>
          <section className="checkout-card checkout-customer-card">
            <div className="checkout-card__title">
              <span>
                <UserRound size={20} />
              </span>
              <div>
                <h2>{t("publicMenu.checkout.customer")}</h2>
                <p>
                  {translateFallback(
                    t,
                    "publicMenu.checkout.customerHint",
                    "Add your contact details so the restaurant can confirm your order.",
                  )}
                </p>
              </div>
            </div>
            <label
              className={`checkout-field ${
                fieldErrors.customer_name ? "checkout-field--error" : ""
              }`}
            >
              <span>{t("orders.table.customer")}</span>
              <div>
                <UserRound size={17} />
                <input
                  value={form.customer_name}
                  placeholder={t("orders.form.customerNamePlaceholder")}
                  aria-invalid={Boolean(fieldErrors.customer_name)}
                  onChange={(event) =>
                    updateForm("customer_name", event.target.value)
                  }
                />
              </div>
              {fieldErrors.customer_name && (
                <small className="checkout-field__error">
                  {fieldErrors.customer_name}
                </small>
              )}
            </label>
            {form.order_type !== "dine_in" && (
            <label
              className={`checkout-field ${
                fieldErrors.customer_phone ? "checkout-field--error" : ""
              }`}
            >
              <span>{t("orders.table.phone")}</span>
              <div>
                <Phone size={17} />
                <input
                  value={form.customer_phone}
                  placeholder={t("orders.form.customerPhonePlaceholder")}
                  aria-invalid={Boolean(fieldErrors.customer_phone)}
                  onChange={(event) =>
                    updateForm("customer_phone", event.target.value)
                  }
                />
              </div>
              {fieldErrors.customer_phone && (
                <small className="checkout-field__error">
                  {fieldErrors.customer_phone}
                </small>
              )}
            </label>
            )}
            <div className="checkout-type-tabs">
              {["dine_in", "takeaway", "delivery"].map((type) => (
                <button
                  key={type}
                  type="button"
                  className={form.order_type === type ? "active" : ""}
                  onClick={() => updateOrderType(type)}
                >
                  {type === "delivery" ? (
                    <Truck size={16} />
                  ) : (
                    <ShoppingBag size={16} />
                  )}
                  {t(`orders.types.${type}`)}
                </button>
              ))}
            </div>
            {form.order_type === "dine_in" && (
              <label
                className={`checkout-field ${
                  fieldErrors.table_number ? "checkout-field--error" : ""
                }`}
              >
                <span>{t("orders.table.tableNumber")}</span>
                <div>
                  <ClipboardList size={17} />
                  <input
                    value={form.table_number}
                    placeholder={t("orders.form.tableNumberPlaceholder")}
                    aria-invalid={Boolean(fieldErrors.table_number)}
                    onChange={(event) =>
                      updateForm("table_number", event.target.value)
                    }
                  />
                </div>
                {fieldErrors.table_number && (
                  <small className="checkout-field__error">
                    {fieldErrors.table_number}
                  </small>
                )}
              </label>
            )}
            <label className="checkout-field checkout-field--textarea">
              <span>{t("orders.form.customerNote")}</span>
              <textarea
                value={form.customer_note}
                placeholder={t("orders.form.customerNotePlaceholder")}
                onChange={(event) =>
                  updateForm("customer_note", event.target.value)
                }
              />
            </label>
          </section>
          <aside className="checkout-card checkout-summary-card">
            <div className="checkout-card__title">
              <span>
                <ReceiptText size={20} />
              </span>
              <div>
                <h2>{t("publicMenu.checkout.summary")}</h2>
                <p>
                  {translateFallback(
                    t,
                    "publicMenu.checkout.summaryHint",
                    "Review your products before submitting the order.",
                  )}
                </p>
              </div>
            </div>
            <div
              className={`checkout-items ${
                fieldErrors.cart ? "checkout-items--error" : ""
              }`}
            >
              {cartItems.map((item) => (
                <article key={item.row_id} className="checkout-item">
                  <div className="checkout-item__main">
                    {item.product_snapshot?.image ? (
                      <img
                        src={item.product_snapshot.image}
                        alt={item.product_snapshot?.name || ""}
                      />
                    ) : (
                      <span>
                        <UtensilsCrossed size={18} />
                      </span>
                    )}
                    <div>
                      <strong>{item.product_snapshot?.name}</strong>
                      {item.product_snapshot?.size_name && (
                        <small>{item.product_snapshot.size_name}</small>
                      )}
                      {item.product_snapshot?.supplements?.length > 0 && (
                        <small>
                          {item.product_snapshot.supplements
                            .map((supplement) => supplement.name)
                            .join(", ")}
                        </small>
                      )}
                    </div>
                  </div>
                  <div className="checkout-item__actions">
                    <div className="checkout-quantity">
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(item.row_id, item.quantity - 1)
                        }
                      >
                        <Minus size={14} />
                      </button>
                      <strong>{item.quantity}</strong>
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(item.row_id, item.quantity + 1)
                        }
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <strong>{formatPrice(item.total_price, currency)}</strong>
                    <button
                      type="button"
                      className="checkout-remove"
                      onClick={() => removeItem(item.row_id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </article>
              ))}
              {!cartItems.length && (
                <div className="checkout-empty">
                  <ShoppingBag size={34} />
                  <strong>{t("publicMenu.checkout.empty")}</strong>
                </div>
              )}
            </div>
            {fieldErrors.cart && (
              <small className="checkout-cart-error">{fieldErrors.cart}</small>
            )}
            <div className="checkout-total">
              <span>{t("orders.details.total")}</span>
              <strong>{formatPrice(totalAmount, currency)}</strong>
            </div>
            <button
              type="submit"
              className="checkout-submit"
              disabled={!canSubmitOrder}
            >
              {saving ? <Loader2 size={18} /> : <Save size={18} />}
              {orderingEnabled
                ? t("publicMenu.checkout.placeOrder")
                : translateFallback(
                    t,
                    "publicMenu.checkout.orderingDisabledButton",
                    "Ordering disabled",
                  )}
            </button>
            {orderingEnabled && cartItems.length > 0 && (
              <div className="checkout-secure-note">
                <CheckCircle2 size={16} />
                {translateFallback(
                  t,
                  "publicMenu.checkout.secureNote",
                  "Your order will be sent directly to the restaurant.",
                )}
              </div>
            )}
          </aside>
        </form>
      </section>
    </main>
  );
}
export default PublicOrderCheckout;
